import { Menu, MenuItem } from "@blueprintjs/core";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { showContextMenu } from "modules/common/services/context-menu";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { endKey, linkCaption, LinkRole, PlanLinkEndResponse, PlanLinkResponse } from "../models/plan-dependencies";
import styles from "./LinksLayer.module.scss";

/** Начало протягивания связи: от какого конца какого объекта, экранные px точки */
export interface LinkDragStart {
    /** endKey объекта: work:<id> или section:<order>/<guid> */
    key: string;
    side: "start" | "end";
    x: number;
    y: number;
}

/** Точка протягивания — насколько снаружи края полосы её центр, px */
const CONNECTOR_OFFSET = 9;

/** Радиус точки и зона нажатия вокруг неё, px */
const CONNECTOR_RADIUS = 6;
const CONNECTOR_HIT = 11;

/** Сколько держать точки после ухода курсора с полосы — чтобы успеть до них дотянуться, мс */
const CONNECTOR_LINGER = 350;

/** Отступ стрелки от края полосы до поворота, px */
const STUB = 8;

/** Длина якоря связи, уходящей за выборку, px */
const ANCHOR = 14;

/**
 * Вертикальный якорь (второй конец выше или ниже экрана): вбок от края полосы на STUB
 * (следующие якоря у того же края — ещё на STEP), затем вверх или вниз на RISE — за
 * границу строки (половина строки 20px), чтобы не упирался в стенку полосы
 */
const ANCHOR_STUB = 14;
const ANCHOR_STEP = 14;
const ANCHOR_RISE = 28;

interface LinksLayerProps {
    links: PlanLinkResponse[];
    /** Контейнер сетки: стрелки рисуются в его координатах */
    gridRef: React.RefObject<HTMLDivElement | null>;
    /** Прокручиваемый контейнер: по его прокрутке слой обрезается под липкими колонкой и шапкой */
    scrollRef: React.RefObject<HTMLDivElement | null>;
    /** Ширина липкой колонки названий вместе с отступом, px без зума */
    trackOffset: number;
    /** Зум сетки (CSS zoom) — экранные px делятся на него */
    zoom: number;
    /** Связи, которые сейчас нарушены (конфликт), — красные */
    violated: Set<string>;
    /** Проекты графика — второй конец вне их лежит за выборкой, для него якорь */
    orders: Set<string>;
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    onRemove: (link: PlanLinkResponse) => void;
    /** Правый клик по стрелке или якорю → «Удалить связь»: спросить подтверждение и удалить */
    onConfirmRemove: (link: PlanLinkResponse) => void;
    /** Клик по якорю: перейти к концу, который за выборкой */
    onGoTo: (end: PlanLinkEndResponse) => void;
    /** Где стоит конец связи по плану с черновиком: первый и последний день */
    placeOf: (end: PlanLinkEndResponse, role?: LinkRole) => { start: string; end: string } | null;
    /**
     * X даты в координатах дорожки (px без зума): левый край дня для начала, правый для
     * конца. null — дата вне сетки
     */
    columnX: (day: string, side: "start" | "end") => number | null;
    /** Протягивание новой связи: от точки у конца полосы к курсору, экранные px */
    preview?: { from: Point; to: Point } | null;
    /**
     * Начать протягивание связи от точки у конца полосы. Точки рисуются здесь, поверх
     * стрелок: в строке проекта они оказывались под широкой зоной клика стрелки и якорями
     * (строки лежат в Collapse с transform — свой слой наложения, z-index там не помогает)
     */
    onStartLink?: (start: LinkDragStart) => void;
}

/** Полоса под курсором, от которой можно протянуть связь, — в координатах сетки */
interface HoverBar {
    key: string;
    left: number;
    right: number;
    y: number;
}

interface Point {
    x: number;
    y: number;
}

/** Точка конца связи; collapsed — полосы объекта не видно, точка в строке раздела или проекта */
interface EndPoint extends Point {
    collapsed?: boolean;
}

interface Drawn {
    link: PlanLinkResponse;
    /** Путь стрелки; null — рисуется только якорь */
    path: string | null;
    /** Концы стрелки в свёрнутой строке: кружок — клик раскроет и приведёт к объекту */
    collapsed?: { at: Point; end: PlanLinkEndResponse }[];
    anchor: {
        at: Point;
        /** Куда смотрит якорь: к невидимому концу (вверх, вниз, вбок) или наружу от полосы */
        dx: number;
        dy: number;
        /** Сторона наружу от края полосы: вертикальный якорь сначала выходит в неё */
        outward: number;
        end: PlanLinkEndResponse;
        outgoing: boolean;
        order: number;
        /** Второй конец в графике есть, но за экраном; false — его нет в выборке */
        offscreen: boolean;
    } | null;
}

/**
 * Стрелки связей поверх сетки (п. 6 ТЗ). Концы — полосы с data-link-end: работа или
 * раздел. Свёрнутый проект — стрелка к его строке. Второй конец за выборкой графика —
 * якорь на краю полосы: подсказка «→ 0468 · КР · Волохова Р.А.», клик — к нему.
 *
 * Координаты меряются по DOM после каждого изменения сетки (MutationObserver, кадр
 * анимации): раскрытие проектов, перетаскивание и смена масштаба двигают полосы, а
 * хранить их позиции отдельно от раскладки значит разъехаться с ней при первой правке.
 */
export const LinksLayer: React.FC<LinksLayerProps> = (props) => {
    const {
        links,
        gridRef,
        scrollRef,
        trackOffset,
        zoom,
        violated,
        orders,
        selectedId,
        onSelect,
        onConfirmRemove,
        onRemove,
        onGoTo,
        placeOf,
        columnX,
        preview,
        onStartLink,
    } = props;

    /* ---------- точки протягивания у полосы под курсором ---------- */

    const [hoverBar, setHoverBar] = useState<HoverBar | null>(null);
    const lingerTimer = useRef(0);

    useEffect(() => {
        const grid = gridRef.current;

        if (!grid || !onStartLink) {
            setHoverBar(null);
            return;
        }

        const onMove = (event: PointerEvent) => {
            const target = event.target as Element | null;

            // курсор на самой точке — держим её
            if (target && target.closest("[data-link-connector]")) {
                window.clearTimeout(lingerTimer.current);
                return;
            }

            const bar = target ? (target.closest("[data-link-connectable]") as HTMLElement | null) : null;

            if (!bar) {
                window.clearTimeout(lingerTimer.current);
                lingerTimer.current = window.setTimeout(() => setHoverBar(null), CONNECTOR_LINGER);
                return;
            }

            window.clearTimeout(lingerTimer.current);

            const origin = grid.getBoundingClientRect();
            const rect = bar.getBoundingClientRect();
            const scale = zoom || 1;
            const next: HoverBar = {
                key: bar.getAttribute("data-link-end") || "",
                left: (rect.left - origin.left) / scale,
                right: (rect.right - origin.left) / scale,
                y: (rect.top + rect.height / 2 - origin.top) / scale,
            };

            setHoverBar((current) =>
                current &&
                current.key === next.key &&
                current.left === next.left &&
                current.right === next.right &&
                current.y === next.y
                    ? current
                    : next
            );
        };

        // курсор ушёл с сетки совсем — точки гаснут, иначе висели бы до следующего движения над ней
        const onLeave = () => {
            window.clearTimeout(lingerTimer.current);
            lingerTimer.current = window.setTimeout(() => setHoverBar(null), CONNECTOR_LINGER);
        };

        grid.addEventListener("pointermove", onMove);
        grid.addEventListener("pointerleave", onLeave);

        return () => {
            grid.removeEventListener("pointermove", onMove);
            grid.removeEventListener("pointerleave", onLeave);
            window.clearTimeout(lingerTimer.current);
        };
    }, [gridRef, zoom, onStartLink]);

    const [drawn, setDrawn] = useState<Drawn[]>([]);
    const [size, setSize] = useState({ width: 0, height: 0 });

    /**
     * Видимая часть дорожки в координатах сетки. Липкие колонка названий и шапка дней
     * подняты только внутри своих блоков, а слой стрелок лежит над блоками — без обрезки
     * стрелки при прокрутке рисовались бы поверх фамилий и дат
     */
    const [clip, setClip] = useState({ left: 0, top: 0 });
    const frame = useRef(0);

    const measure = useCallback(() => {
        const grid = gridRef.current;

        if (!grid) {
            return;
        }

        const origin = grid.getBoundingClientRect();
        const scale = zoom || 1;
        const local = (rect: DOMRect) => ({
            left: (rect.left - origin.left) / scale,
            right: (rect.right - origin.left) / scale,
            middle: (rect.top + rect.height / 2 - origin.top) / scale,
        });

        /**
         * Край набора полос: у «старта» — левый край самой левой, у «финиша» — правый самой
         * правой. span — только полосы в этих x (кусок разорванной работы)
         */
        const edgeOfBars = (
            selector: string,
            side: "start" | "end",
            span?: { left: number; right: number } | null
        ): Point | null => {
            const all = Array.from(grid.querySelectorAll<HTMLElement>(selector))
                .map((element) => local(element.getBoundingClientRect()))
                .filter((rect) => rect.right > rect.left);
            const inside = span ? all.filter((rect) => rect.right > span.left + 1 && rect.left < span.right - 1) : [];
            const bars = inside.length ? inside : all;

            if (!bars.length) {
                return null;
            }

            const bar = bars.reduce((best, rect) =>
                side === "start" ? (rect.left < best.left ? rect : best) : rect.right > best.right ? rect : best
            );

            return { x: side === "start" ? bar.left : bar.right, y: bar.middle };
        };

        // левый край дорожки в координатах сетки: к нему прибавляется x даты
        const track = grid.querySelector<HTMLElement>("[data-plan-track]");
        const trackLeft = track ? local(track.getBoundingClientRect()).left : trackOffset;

        /**
         * Полосы самого объекта на экране нет (строки сотрудников свёрнуты, режим «Разделы»,
         * свёрнут проект) — стрелка идёт к строке его раздела, а если и её нет — к строке
         * проекта. По горизонтали — к дате начала или конца объекта: край чужой полосы
         * (например, прошлой части проекта) увёл бы стрелку к самой колонке названий
         */
        const rowOf = (end: PlanLinkEndResponse): HTMLElement | null =>
            (end.contentGuid !== undefined
                ? grid.querySelector<HTMLElement>(`[data-link-section="${end.orderId}/${end.contentGuid || ""}"]`)
                : null) || grid.querySelector<HTMLElement>(`[data-project-row="${end.orderId}"]`);

        /** x куска работы, к которому относится связь (у разорванной работы) */
        const spanOf = (end: PlanLinkEndResponse, role: LinkRole) => {
            const part = end.kind === "work" ? placeOf(end, role) : null;
            const left = part ? columnX(part.start, "start") : null;
            const right = part ? columnX(part.end, "end") : null;

            return left !== null && right !== null ? { left: trackLeft + left, right: trackLeft + right } : null;
        };

        const edgeOf = (end: PlanLinkEndResponse, side: "start" | "end", role: LinkRole): EndPoint | null => {
            const bar = edgeOfBars(`[data-link-end="${endKey(end)}"]`, side, spanOf(end, role));

            if (bar) {
                return bar;
            }

            const row = rowOf(end);
            const place = placeOf(end, role);
            const x = place ? columnX(side === "start" ? place.start : place.end, side) : null;

            return row && x !== null
                ? { x: trackLeft + x, y: local(row.getBoundingClientRect()).middle, collapsed: true }
                : null;
        };

        const result: Drawn[] = [];
        const stacked = new Map<string, number>();

        // Видимая часть дорожки в координатах сетки: правее колонки названий и ниже шапки.
        // Стрелка рисуется, только когда на экране оба конца (п. 6 ТЗ: «стрелки внутри видимой
        // области как обычно»), иначе у видимого конца — якорь к невидимому
        const scroller = scrollRef.current;
        const header = grid.firstElementChild as HTMLElement | null;
        const view = scroller
            ? (() => {
                  const rect = scroller.getBoundingClientRect();
                  return {
                      left: (rect.left - origin.left) / scale + trackOffset,
                      right: (rect.right - origin.left) / scale,
                      top:
                          (rect.top - origin.top) / scale +
                          (header ? header.getBoundingClientRect().height / scale : 0),
                      bottom: (rect.bottom - origin.top) / scale,
                  };
              })()
            : null;

        const inView = (point: Point) =>
            !view ||
            (point.x >= view.left - 2 && point.x <= view.right + 2 && point.y >= view.top && point.y <= view.bottom);

        const pushAnchor = (
            link: PlanLinkResponse,
            at: Point,
            dx: number,
            dy: number,
            outward: number,
            end: PlanLinkEndResponse,
            outgoing: boolean,
            offscreen: boolean
        ) => {
            // несколько якорей у одного края полосы в одну сторону — в ряд, а не друг на друге
            const place = `${Math.round(at.x)}:${Math.round(at.y)}:${dx}:${dy}`;
            const order = (stacked.get(place) || 0) + 1;
            stacked.set(place, order);

            result.push({ link, path: null, anchor: { at, dx, dy, outward, end, outgoing, order, offscreen } });
        };

        /** Направление к невидимому концу: вверх или вниз, если он за экраном по вертикали, иначе вбок */
        const toward = (at: Point, other: Point) =>
            view && other.y < view.top
                ? { dx: 0, dy: -1 }
                : view && other.y > view.bottom
                  ? { dx: 0, dy: 1 }
                  : { dx: other.x >= at.x ? 1 : -1, dy: 0 };

        links.forEach((link) => {
            if (!link.from || !link.to) {
                return;
            }

            const fromSide = link.type === "fs" || link.type === "ff" ? "end" : "start";
            const toSide = link.type === "fs" || link.type === "ss" ? "start" : "end";

            const from = orders.has(link.from.orderId) ? edgeOf(link.from, fromSide, "from") : null;
            const to = orders.has(link.to.orderId) ? edgeOf(link.to, toSide, "to") : null;

            if (from && to) {
                const seeFrom = inView(from);
                const seeTo = inView(to);

                // оба конца свёрнуты в одну строку (связь внутри свёрнутого проекта или раздела) —
                // стрелка в пустоте ничего не объясняет, её видно, если раскрыть
                if (from.collapsed && to.collapsed && Math.abs(from.y - to.y) < 1) {
                    return;
                }

                if (seeFrom && seeTo) {
                    const collapsed = [
                        ...(from.collapsed ? [{ at: { x: from.x, y: from.y }, end: link.from }] : []),
                        ...(to.collapsed ? [{ at: { x: to.x, y: to.y }, end: link.to }] : []),
                    ];

                    result.push({ link, path: route(from, fromSide, to, toSide), anchor: null, collapsed });
                } else if (seeFrom) {
                    const dir = toward(from, to);
                    pushAnchor(link, from, dir.dx, dir.dy, fromSide === "end" ? 1 : -1, link.to, true, true);
                } else if (seeTo) {
                    const dir = toward(to, from);
                    pushAnchor(link, to, dir.dx, dir.dy, toSide === "start" ? -1 : 1, link.from, false, true);
                }

                return;
            }

            // второй конец за выборкой графика — якорь у того, что на экране, наружу от полосы
            if (from && inView(from)) {
                pushAnchor(
                    link,
                    from,
                    fromSide === "end" ? 1 : -1,
                    0,
                    fromSide === "end" ? 1 : -1,
                    link.to,
                    true,
                    false
                );
            } else if (to && inView(to)) {
                pushAnchor(
                    link,
                    to,
                    toSide === "start" ? -1 : 1,
                    0,
                    toSide === "start" ? -1 : 1,
                    link.from,
                    false,
                    false
                );
            }
        });

        setDrawn(result);
        setSize({ width: grid.scrollWidth / 1, height: grid.scrollHeight / 1 });
    }, [links, gridRef, scrollRef, zoom, orders, placeOf, columnX, trackOffset]);

    const measureClip = useCallback(() => {
        const scroller = scrollRef.current;
        const grid = gridRef.current;

        if (!scroller || !grid) {
            return;
        }

        const scale = zoom || 1;
        // шапка дней — первый ребёнок сетки
        const header = grid.firstElementChild as HTMLElement | null;
        const headerHeight = header ? header.getBoundingClientRect().height / scale : 0;

        const next = {
            left: scroller.scrollLeft / scale + trackOffset,
            top: scroller.scrollTop / scale + headerHeight,
        };

        setClip((current) => (current.left === next.left && current.top === next.top ? current : next));
    }, [scrollRef, gridRef, zoom, trackOffset]);

    useEffect(() => {
        const scroller = scrollRef.current;

        measureClip();

        if (!scroller) {
            return;
        }

        scroller.addEventListener("scroll", measureClip, { passive: true });
        return () => scroller.removeEventListener("scroll", measureClip);
    }, [scrollRef, measureClip]);

    const schedule = useCallback(() => {
        window.cancelAnimationFrame(frame.current);
        frame.current = window.requestAnimationFrame(measure);
    }, [measure]);

    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        const grid = gridRef.current;

        if (!grid) {
            return;
        }

        const observer = new MutationObserver(schedule);
        observer.observe(grid, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "class"],
        });
        window.addEventListener("resize", schedule);
        // что на экране, а что за ним, меняется при прокрутке: стрелка ↔ якорь
        const scroller = scrollRef.current;
        scroller && scroller.addEventListener("scroll", schedule, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", schedule);
            scroller && scroller.removeEventListener("scroll", schedule);
            window.cancelAnimationFrame(frame.current);
        };
    }, [gridRef, scrollRef, schedule]);

    // выбранная стрелка удаляется клавишей Delete, Esc снимает выбор
    useEffect(() => {
        if (!selectedId) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onSelect(null);
            }

            if (event.key === "Delete" || event.key === "Backspace") {
                const link = links.find((item) => item.id === selectedId);

                if (link && link.canEdit) {
                    event.preventDefault();
                    onRemove(link);
                }
            }
        };

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Element | null;

            if (!target || !target.closest("[data-link-hit]")) {
                onSelect(null);
            }
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [selectedId, links, onSelect, onRemove]);

    /**
     * Клик по якорю (или свёрнутому концу) — перейти ко второму концу, выбрав связь: после
     * перехода она видна либо стрелкой, либо якорем у другого конца, и её надо узнать
     */
    const goTo = (link: PlanLinkResponse, end: PlanLinkEndResponse) => {
        onSelect(link.id);
        onGoTo(end);
    };

    /** Правый клик по стрелке или якорю: выбрать связь, у редактируемой — меню «Удалить связь» */
    const openMenu = (event: React.MouseEvent, link: PlanLinkResponse) => {
        event.preventDefault();
        event.stopPropagation();
        onSelect(link.id);

        if (!link.canEdit) {
            return;
        }

        showContextMenu({
            content: (
                <Menu>
                    <MenuItem
                        icon={<GeneralIcon type="general-trash" />}
                        text="Удалить связь"
                        onClick={() => onConfirmRemove(link)}
                    />
                </Menu>
            ),
            targetOffset: { left: event.clientX, top: event.clientY },
        });
    };

    return (
        <svg
            className={styles.layer}
            width={size.width}
            height={size.height}
            style={{ clipPath: `inset(${clip.top}px 0 0 ${clip.left}px)` }}
        >
            <defs>
                {(["normal", "violated", "selected"] as const).map((kind) => (
                    // размер наконечника — в px, а не в толщинах линии: иначе у толстой
                    // выбранной стрелки он раздувался бы вдвое
                    <marker
                        key={kind}
                        id={`plan-link-arrow-${kind}`}
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerUnits="userSpaceOnUse"
                        markerWidth="11"
                        markerHeight="11"
                        orient="auto-start-reverse"
                    >
                        <path
                            d="M0,0 L10,5 L0,10 z"
                            className={styles[`head_${kind}`]}
                            stroke="#fff"
                            strokeWidth="1"
                            strokeLinejoin="round"
                        />
                    </marker>
                ))}
            </defs>

            {preview && gridRef.current && <PreviewLine grid={gridRef.current} zoom={zoom} {...preview} />}

            {drawn.map(({ link, path, anchor, collapsed }) => {
                const kind = link.id === selectedId ? "selected" : violated.has(link.id) ? "violated" : "normal";
                const tip = `${linkCaption(link.type, link.lag)}: ${link.from ? link.from.label : ""} → ${
                    link.to ? link.to.label : ""
                }`;
                const note = [
                    violated.has(link.id) ? "Связь нарушена" : "",
                    link.canEdit ? "Клик — выбрать, Delete или правый клик — удалить" : "",
                    "Двойной клик — к связанному объекту",
                ]
                    .filter(Boolean)
                    .join(" · ");

                if (path) {
                    return (
                        <g key={link.id}>
                            {/* белая кайма под стрелкой — читается на любом цвете полосы */}
                            <path d={path} className={styles.halo} />
                            <path
                                d={path}
                                className={`${styles.line} ${styles[`line_${kind}`]}`}
                                markerEnd={`url(#plan-link-arrow-${kind})`}
                            />
                            {/* широкая прозрачная линия — чтобы в тонкую стрелку можно было попасть */}
                            <path
                                d={path}
                                className={styles.hit}
                                data-link-hit={link.id}
                                data-tip={tip}
                                data-tip-note={note}
                                onClick={() => onSelect(link.id)}
                                onContextMenu={(event) => openMenu(event, link)}
                                onDoubleClick={() => {
                                    // двойной клик — к свёрнутому концу, а если оба видны — к последователю
                                    const target = collapsed && collapsed.length ? collapsed[0].end : link.to;
                                    target && onGoTo(target);
                                }}
                            />

                            {/* конец в свёрнутой строке: невидимая зона клика — раскрыть и перейти к объекту.
                                Кружка нет: его путали с точкой протягивания */}
                            {(collapsed || []).map((item) => (
                                <g
                                    key={endKey(item.end)}
                                    className={styles.anchor}
                                    data-link-hit={link.id}
                                    data-tip={item.end.label}
                                    data-tip-note="Свёрнуто — клик раскроет и переведёт к объекту"
                                    onClick={() => goTo(link, item.end)}
                                    onContextMenu={(event) => openMenu(event, link)}
                                >
                                    <rect
                                        x={item.at.x - 8}
                                        y={item.at.y - 8}
                                        width={16}
                                        height={16}
                                        className={styles.anchorHit}
                                    />
                                </g>
                            ))}
                        </g>
                    );
                }

                if (!anchor) {
                    return null;
                }

                const anchorTip = `${linkCaption(link.type, link.lag)} ${anchor.outgoing ? "→" : "←"} ${anchor.end.label}`;
                const anchorNote = anchor.offscreen
                    ? "Связанный объект за экраном — клик прокрутит к нему"
                    : "Связанный объект за пределами графика — клик расширит выборку и переведёт к нему";

                if (anchor.dy) {
                    // Вверх или вниз: уголок — выход вбок от края полосы, затем за границу строки,
                    // на конце кружок. Несколько якорей у одного края расходятся вбок, чтобы не
                    // слиться в одну линию
                    const x = anchor.at.x + anchor.outward * (ANCHOR_STUB + ANCHOR_STEP * (anchor.order - 1));
                    const y = anchor.at.y + anchor.dy * ANCHOR_RISE;
                    const d = `M${anchor.at.x},${anchor.at.y} H${x} V${y}`;

                    return (
                        <g
                            key={link.id}
                            className={styles.anchor}
                            data-link-hit={link.id}
                            data-tip={anchorTip}
                            data-tip-note={anchorNote}
                            onClick={() => goTo(link, anchor.end)}
                            onContextMenu={(event) => openMenu(event, link)}
                        >
                            <path d={d} className={styles.halo} />
                            <path d={d} className={`${styles.line} ${styles[`line_${kind}`]}`} />
                            <circle cx={x} cy={y} r={5} className={styles[`dot_${kind}`]} />
                            {/* зона клика — вокруг кружка и вертикального отрезка */}
                            <rect
                                x={x - 8}
                                y={Math.min(anchor.at.y, y) - 8}
                                width={16}
                                height={ANCHOR_RISE + 16}
                                className={styles.anchorHit}
                            />
                        </g>
                    );
                }

                // вбок: каждый следующий якорь у того же края — длиннее на шаг, кружки в ряд
                const x2 = anchor.at.x + anchor.dx * ANCHOR * anchor.order;
                const y2 = anchor.at.y;

                return (
                    <g
                        key={link.id}
                        className={styles.anchor}
                        data-link-hit={link.id}
                        data-tip={anchorTip}
                        data-tip-note={anchorNote}
                        onClick={() => goTo(link, anchor.end)}
                        onContextMenu={(event) => openMenu(event, link)}
                    >
                        <line x1={anchor.at.x} y1={anchor.at.y} x2={x2} y2={y2} className={styles.halo} />
                        <line
                            x1={anchor.at.x}
                            y1={anchor.at.y}
                            x2={x2}
                            y2={y2}
                            className={`${styles.line} ${styles[`line_${kind}`]}`}
                        />
                        <circle cx={x2} cy={y2} r={5} className={styles[`dot_${kind}`]} />
                        {/* зона клика — только вокруг своего кружка, чтобы не перекрывать соседний якорь */}
                        <rect x={x2 - 8} y={y2 - 8} width={16} height={16} className={styles.anchorHit} />
                    </g>
                );
            })}
            {/* точки протягивания — последними, поверх стрелок и якорей */}
            {hoverBar &&
                onStartLink &&
                !preview &&
                (["start", "end"] as const).map((side) => {
                    const cx = side === "start" ? hoverBar.left - CONNECTOR_OFFSET : hoverBar.right + CONNECTOR_OFFSET;

                    return (
                        <g
                            key={side}
                            className={styles.connector}
                            data-link-connector={side}
                            data-tip={side === "start" ? "Связь от начала" : "Связь от конца"}
                            data-tip-note={
                                side === "start"
                                    ? "Тяните к другой полосе: к её началу — СС, к концу — СФ"
                                    : "Тяните к другой полосе: к её началу — ФС, к концу — ФФ"
                            }
                            onPointerDown={(event) => {
                                if (event.button !== 0) {
                                    return;
                                }

                                event.preventDefault();
                                event.stopPropagation();

                                const rect = (event.currentTarget as SVGGElement).getBoundingClientRect();
                                onStartLink({
                                    key: hoverBar.key,
                                    side,
                                    x: rect.left + rect.width / 2,
                                    y: rect.top + rect.height / 2,
                                });
                                setHoverBar(null);
                            }}
                            onMouseDown={(event) => event.preventDefault()}
                        >
                            <circle cx={cx} cy={hoverBar.y} r={CONNECTOR_HIT} className={styles.connectorHit} />
                            <circle cx={cx} cy={hoverBar.y} r={CONNECTOR_RADIUS} className={styles.connectorDot} />
                        </g>
                    );
                })}
        </svg>
    );
};

/** Пунктир протягиваемой связи — от точки у конца полосы до курсора */
const PreviewLine: React.FC<{ grid: HTMLDivElement; zoom: number; from: Point; to: Point }> = ({
    grid,
    zoom,
    from,
    to,
}) => {
    const origin = grid.getBoundingClientRect();
    const scale = zoom || 1;
    const x1 = (from.x - origin.left) / scale;
    const y1 = (from.y - origin.top) / scale;
    const x2 = (to.x - origin.left) / scale;
    const y2 = (to.y - origin.top) / scale;

    return (
        <g>
            <line x1={x1} y1={y1} x2={x2} y2={y2} className={styles.halo} />
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                className={`${styles.line} ${styles.preview}`}
                markerEnd="url(#plan-link-arrow-selected)"
            />
        </g>
    );
};

/**
 * Ломаная от края предшественника к краю последователя: выход в сторону края, поворот,
 * вход в последователя со стороны его края. Если по горизонтали места нет (последователь
 * левее), стрелка обходит между строками.
 */
function route(from: Point, fromSide: "start" | "end", to: Point, toSide: "start" | "end"): string {
    const out = from.x + (fromSide === "end" ? STUB : -STUB);
    const enter = to.x + (toSide === "start" ? -STUB : STUB);

    const direct = toSide === "start" ? enter >= out : enter <= out;

    if (direct) {
        return `M${from.x},${from.y} H${out} V${to.y} H${to.x}`;
    }

    // между строками: по середине, а в одной строке — над ней
    const between = from.y === to.y ? from.y - 16 : (from.y + to.y) / 2;

    return `M${from.x},${from.y} H${out} V${between} H${enter} V${to.y} H${to.x}`;
}
